import React, { useState, useEffect } from 'react';
import { ArrowRight, Users, Award, Target, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import Header from './Header';
import Footer from './Footer';
import WhatsAppWidget from './WhatsAppWidget';
import { useLocation } from 'react-router-dom';

const CarrierePage = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    nome_cognome: '',
    email: '',
    telefono: '',
    posizione_interesse: '',
    messaggio: '',
    cv_file: null
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Get backend URL from environment
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/carriere-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Errore nell\'invio della candidatura');
      }

      toast({
        title: "Candidatura inviata con successo!",
        description: "Ti contatteremo entro 48 ore per valutare il tuo profilo.",
      });

      // Reset form
      setFormData({
        nome_cognome: '',
        email: '',
        telefono: '',
        posizione_interesse: '',
        messaggio: '',
        cv_file: null
      });

    } catch (error) {
      toast({
        title: "Errore nell'invio",
        description: "Si è verificato un errore. Riprova o contattaci direttamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <WhatsAppWidget />
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1521737711867-e3b97375f902')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay'
        }}
      >
        <div className="absolute inset-0 bg-black opacity-70"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-white">Lavora con </span>
            <span className="text-orange-500">Noi</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Unisciti al team Lovecchio Group e costruisci il futuro delle telecomunicazioni business
          </p>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">Perché Lovecchio Group?</h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Siamo un'azienda in crescita che offre opportunità concrete di sviluppo professionale
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-8 bg-gray-800 rounded-lg">
              <Users className="w-16 h-16 text-orange-500 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Team Dinamico</h3>
              <p className="text-gray-400">
                Lavora con professionisti esperti in un ambiente stimolante e collaborativo
              </p>
            </div>

            <div className="text-center p-8 bg-gray-800 rounded-lg">
              <Award className="w-16 h-16 text-blue-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Formazione Continua</h3>
              <p className="text-gray-400">
                Investiamo nella tua crescita con corsi di aggiornamento e certificazioni
              </p>
            </div>

            <div className="text-center p-8 bg-gray-800 rounded-lg">
              <Target className="w-16 h-16 text-green-400 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Obiettivi Chiari</h3>
              <p className="text-gray-400">
                Sistema di incentivi basato sui risultati e possibilità di crescita concrete
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-6">Candidati Ora</h2>
              <p className="text-gray-400 text-lg">
                Invia la tua candidatura e entra a far parte del nostro team
              </p>
            </div>

            <div className="bg-gray-800 rounded-lg p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Nome e Cognome *
                  </label>
                  <input
                    type="text"
                    name="nome_cognome"
                    value={formData.nome_cognome}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                    placeholder="Mario Rossi"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                      placeholder="mario.rossi@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">
                      Telefono *
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                      placeholder="+39 333 1234567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Posizione di Interesse
                  </label>
                  <select
                    name="posizione_interesse"
                    value={formData.posizione_interesse}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                  >
                    <option value="">Seleziona una posizione</option>
                    <option value="Consulente Commerciale">Consulente Commerciale</option>
                    <option value="Account Manager">Account Manager</option>
                    <option value="Tecnico Specializzato">Tecnico Specializzato</option>
                    <option value="Customer Service">Customer Service</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Amministrazione">Amministrazione</option>
                    <option value="Altro">Altro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Messaggio (Opzionale)
                  </label>
                  <textarea
                    name="messaggio"
                    value={formData.messaggio}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-orange-500 focus:outline-none transition-colors"
                    placeholder="Presentati brevemente e descrivi le tue competenze..."
                  />
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-8 py-4 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-300 inline-flex items-center justify-center"
                  >
                    {isLoading ? 'Invio in corso...' : 'Invia Candidatura'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Hai domande?</h3>
          <p className="text-gray-400 mb-6">
            Contattaci per maggiori informazioni sulle opportunità di lavoro
          </p>
          <div className="flex justify-center items-center space-x-6 text-gray-400">
            <span>📞 333.134.1439</span>
            <span>✉️ info@lovecchiogroup.it</span>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CarrierePage;